/**
 * Regional jurisdiction labelling for citation sources.
 *
 * Authoritative classification lives in ml-scribe — see
 * `service/chat/source_region_matching.py::regional_relevance`. The BE
 * resolves the user's country/jurisdiction with pycountry +
 * hdx-python-country and emits a tri-state `regional_relevance` field on
 * every citation it streams (`"in_region"` / `"global"` / `"out_of_region"`).
 * This module is just the thin wire-to-UI mapping.
 *
 * The BE omits `regional_relevance` when it can't resolve the user's
 * country (anonymous / temp / pre-onboarding accounts), so its absence is
 * the "no chip" signal.
 */

/**
 * Backend-classified relevance bucket streamed on each citation
 * (`Reference.regional_relevance`). Wire form (snake-case) of the BE
 * `RegionalRelevance` literal in `service/chat/source_region_matching.py`.
 */
export type RegionalRelevance = 'in_region' | 'global' | 'out_of_region';

/**
 * UI-side state for the jurisdiction chip. Camel-case form of the BE
 * wire vocab so it composes with the rest of the codebase and the i18n
 * keys (`chat.inRegion`, `chat.outOfRegion`).
 */
export type JurisdictionState = 'inRegion' | 'global' | 'outOfRegion';

export interface Jurisdiction {
  state: JurisdictionState;
}

/**
 * Map the BE-streamed `regional_relevance` to the UI's tri-state chip
 * state. Returns null when the BE didn't classify (typically because the
 * user has no resolved country) so the FE hides the chip rather than
 * guessing.
 */
export function getJurisdiction(
  regionalRelevance: RegionalRelevance | null | undefined
): Jurisdiction | null {
  if (regionalRelevance === 'in_region') return { state: 'inRegion' };
  if (regionalRelevance === 'global') return { state: 'global' };
  if (regionalRelevance === 'out_of_region') return { state: 'outOfRegion' };
  return null;
}
